import type { ControlPeriod, CoverageRule, PeriodMode } from '@shared/control-period';
import type { LinkReason } from '@shared/document-validity';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: ValidationError[];
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

// Authentication types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  expiresIn: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  farmProfile: {
    farmName: string;
    organizationNumber?: string;
    region: string;
    productionTypes: string[];
    certifications: string[];
  };
}

// User types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  createdAt: string;
  lastLogin?: string;
  farmProfile?: FarmProfile;
  role?: UserRole;
  isActive?: boolean;
}

export enum UserRole {
  AGROKOLL_ADMIN = 'AGROKOLL_ADMIN',
  AGROKOLL_OWNER = 'AGROKOLL_OWNER',
  AGROKOLL_RADGIVARE = 'AGROKOLL_RADGIVARE',
  AGROKOLL_B2B = 'AGROKOLL_B2B',
  AGROKOLL_SYSTEM_ADMIN = 'AGROKOLL_SYSTEM_ADMIN',
}

export interface Permission {
  id: string;
  name: string;
  description?: string;
}

export interface RoleInfo {
  role: UserRole | null;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole) => boolean;
}

export interface FarmProfile {
  id: string;
  farmName: string;
  organizationNumber?: string;
  region: string;
  productionTypes: string[];
  certifications: string[];
}

// Admin User Management types
export interface AdminUser {
  id: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
  farmProfile?: FarmProfile;
}

export interface AdminUserDetails extends AdminUser {
  updatedAt: string;
  farmProfile: FarmProfile; // Required in detail view
}

export interface CreateUserPayload {
  email: string;
  password: string;
  role: UserRole;
  isActive?: boolean;
  farmName: string;
  organizationNumber?: string;
  region: string;
  productionTypes: string[];
  certifications: string[];
}

export interface UpdateUserPayload {
  email?: string;
  role?: UserRole;
  isActive?: boolean;
  farmName?: string;
  organizationNumber?: string;
  region?: string;
  productionTypes?: string[];
  certifications?: string[];
}

// Tag and validity types
/** Validity semantics for a document: year-labelled journals, concrete end date,
 *  or standing (tills vidare). Pre-selected by the requirement or upload field,
 *  chosen by the farmer, stored on DocumentMetadata. */
export type CategoryValidityMode = 'annual' | 'expires' | 'none';

/** A tag from the curated vocabulary. `slug` is the identity and never changes. */
export interface Tag {
  slug: string;
  displayName: string;
  /** AREA | KIND | TOPIC — groups the picker; no behaviour depends on it. */
  dimension: string;
  sortOrder: number;
  isActive: boolean;
  usage?: {
    documents: number;
    requirements: number;
    /** Upload-enabled form fields whose `uploadTags` name the slug. */
    uploadFields: number;
  };
}

/** A tag as the PUBLIC share view receives it — an auditor cannot resolve slugs. */
export interface NamedTag {
  slug: string;
  displayName: string;
}

// Document types
export interface Document {
  id: string;
  filename: string;
  originalName: string;
  /**
   * Tag slugs. Replaces the single category (AG-249). Empty = "Okategoriserad".
   * Display names come from the vocabulary (`useTags`), not from here — the
   * public share view is the only place the API sends names along.
   */
  tags: string[];
  /**
   * The document's tag sets ("taggrupper"), each a sorted slug list. `tags` is
   * their union. A document answers a requirement when ANY set covers the
   * requirement's tags and sits on the pärm shelf of each set. Optional only for
   * the deploy window where the API predates the field — read it through
   * `documentTagSets(doc)`, never directly.
   */
  tagSets?: string[][];
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  metadata?: DocumentMetadata;
  // Versioning — see backend Document.previousVersionId.
  previousVersionId?: string | null;
  hasNewerVersion?: boolean;
  hasMultipleVersions?: boolean;
  /**
   * Affarspartner (company) ids the document is tagged with. Empty array
   * or missing = shared doc, visible in every per-company pärm filter.
   * Used by the CompanyPicker in DocumentsPage.
   */
  affarspartnerIds?: string[];
  // Encryption metadata (optional - managed by backend, included for transparency)
  encryptionMetadata?: {
    compressed: boolean;
    algorithm?: 'gzip';
    originalSize: number;
    compressedSize?: number;
    encryptedSize: number;
  };
}

export interface DocumentMetadata {
  /**
   * The validity mode the farmer chose: 'annual' | 'expires' | 'none'.
   * Stored since AG-249 rather than derived from the date string, which made a
   * certificate expiring 31 December indistinguishable from an annual journal
   * (AG-284).
   */
  validityMode?: CategoryValidityMode | null;
  id: string;
  validFromDate?: string;
  validToDate?: string;
  /** Explicit reporting years an annual document covers — supports
   *  non-contiguous selections (e.g. 2023 + 2025). Empty = derive from
   *  the dates. */
  coveredYears?: number[];
  certificationType?: string;
  relatedProductionType?: string;
  customTags: string[];
}

export interface DocumentUploadRequest {
  /** Tag slugs for the uploaded document (its first tag set). Empty = "Okategoriserad". */
  tags: string[];
  /** Pärm upload: link the document to every open, unshared control its tags match. */
  autoLink?: boolean;
  metadata?: {
    validFromDate?: string | null;
    validToDate?: string | null;
    /** Omitted → unchanged (update); [] → explicit clear. */
    coveredYears?: number[];
    /** The mode the farmer chose. Omitted → unchanged (update); null → clear. */
    validityMode?: CategoryValidityMode | null;
    isConfidential?: boolean;
    certificationType?: string;
    relatedProductionType?: string;
    customTags?: string[];
  };
  allowDuplicate?: boolean;
  affarspartnerIds?: string[];
}

export interface DocumentUploadResponse {
  uploaded: Document[];
  duplicates?: {
    filename: string;
    existingDocument: Document;
  }[];
  errors?: {
    filename: string;
    error: string;
  }[];
  /** The controls a pärm upload (`autoLink`) was linked to, merged across files; null when no run happened. */
  crossControlLinks?: CrossControlLinkSummary | null;
}

// Issuer types (Certification organizations)
export interface Issuer {
  id: string;
  code: string;
  displayName: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    controlTypes: number;
  };
}

export interface CreateIssuerRequest {
  code: string;
  displayName: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface UpdateIssuerRequest {
  code?: string;
  displayName?: string;
  description?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isActive?: boolean;
}

// Control System - Kontrollinstanser från fördefinierade kontrolltyper
export interface ControlInstance {
  id: string;
  name: string; // t.ex. "KRAV-kontroll 2025-09-15"
  description?: string;
  controlTypeId: string; // Referens till fördefinierad kontrolltyp
  scheduledDate?: string | null;
  comment?: string; // Optional user comment about this control (max 1000 chars)
  status: ControlStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  agronodkontoId?: string; // Agronod account ID from token
  agronodkontoNamn?: string; // Farm/account name from token
  affarspartnerIds: string[];
  affarspartnerNames: string[];
}

// Unified Control Types with Issuer (replaces predefined control types)
export interface ControlType {
  id: string;
  name: string; // t.ex. "KRAV Ekologisk Certifiering"
  description: string;
  issuerId: string;
  issuer: Issuer; // Certification organization (KRAV, IP, EU Organic, etc.)
  version: string;
  isActive: boolean;
  activeFrom: string;
  activeUntil?: string;
  formConfiguration?: any; // Dynamic form configuration
  documentRequirements?: DocumentRequirement[]; // Document requirements for this control type
  inspectionFrequency?: string;
  notes?: string;
  /** The control period, relative to the control date (AG-298); see shared/control-period. */
  periodMode: PeriodMode;
  periodYearOffset: number;
  periodMonths: number | null;
  requiresFarmProfile?: boolean; // Admin-configured flag; the post-create onboarding prompt is NOT gated on it (see resolvePostCreateDialog)
  createdAt: string;
  updatedAt: string;
  documentRequirementsCount?: number; // Count of document requirements (from backend DTO)
  _count?: {
    controls: number;
    documentRequirements: number; // Deprecated: Use documentRequirementsCount instead
  };
}

// Confirmed document interface (actual ControlDocument links)
export interface ConfirmedDocument extends Document {
  confidence?: number;
  isManual?: boolean;
  linkedAt: string;
  requirementId?: string; // Connection to specific requirement
  // Cross-control document sharing fields
  sourceType?: SourceType; // UPLOADED or LINKED
  sourceControlId?: string | null; // ID of source control (for LINKED documents)
  sourceControlName?: string | null; // Name of source control (for LINKED documents)
  /**
   * Why this link was made automatically at creation (AG-253); null for manual
   * links and links made before AG-253. The reason text is derived from the
   * document's metadata with assessDocumentForRequirement + describeAssessment.
   */
  linkReason?: LinkReason | null;
}

// Bakåtkompatibilitet - behåll för tillfället under refaktoreringen
export interface Control extends ControlInstance {
  controlType: ControlType;
  issuer?: Issuer; // Issuer returned directly from backend
  // New hybrid fields
  confirmedDocuments?: ConfirmedDocument[]; // Actually linked documents
  documentRequirements?: DocumentRequirement[]; // Document requirements from controlType
  formConfiguration?: FormConfiguration; // Dynamic form configuration from controlType (returned as separate field)
  requirementStatuses?: RequirementStatus[]; // Verification status for each requirement
  /** Handling + comment for upload-enabled form fields, keyed by fieldId. */
  formFieldStatuses?: FormFieldStatus[];
  hasActiveShare?: boolean; // Whether this control has been shared with an active share link
  /** IDs of requirements the farmer has manually activated despite being filtered by farm profile. */
  activatedRequirementIds?: string[];
  /** IDs of form fields the farmer has manually activated despite being filtered by farm profile. */
  activatedFieldIds?: string[];
  /** Documents attached to upload-enabled form fields. Kept separate from confirmedDocuments. */
  fieldDocuments?: import('../utils/controls/indexFieldDocuments').FieldDocument[];
  /**
   * The period this control assesses, resolved by the backend from the type's
   * configuration and the control date (shared/control-period). Always sent on
   * the detail endpoint; estimated from today and flagged when the date is missing.
   */
  controlPeriod?: ControlPeriod;
  /**
   * Only on the creation response: what the previous control of the same type
   * offers for the farmer to confirm (AG-253). Absent when there is no previous
   * control or it offers nothing. Nothing in it is written yet.
   */
  renewalProposal?: RenewalProposal;
  /**
   * Only on the detail endpoint: true while the control is an untouched DRAFT
   * and a worked-in previous control of the same type exists (AG-253; an
   * abandoned bare draft does not count). The page then offers "Hämta från
   * förra kontrollen" and fetches the proposal when it is opened; the proposal
   * may still hold nothing to decide.
   */
  renewalAvailable?: boolean;
}

export interface DocumentRequirement {
  id: string;
  /** Tag slugs this requirement asks for; a document matches on superset. */
  tags: string[];
  /** Validity pre-selected on upload: 'annual' | 'expires' | 'none'. */
  validityMode?: CategoryValidityMode | null;
  isRequired: boolean;
  /** Months a document stays valid; 'expires' mode only. */
  validityPeriod?: number;
  /** Annual mode: years relative to the control period's main year (AG-298). */
  yearOffsets: number[];
  /** Annual mode: every year in the window, or any one of them. */
  coverageRule: CoverageRule;
  /**
   * The concrete years this requirement's documents must cover in the control
   * the requirement was fetched with (`controlPeriod.mainYear + yearOffsets`).
   * Only the control endpoints send it; admin responses have no control to
   * resolve against. Empty unless the requirement is annual with a window.
   */
  requirementYears?: number[];
  description: string;
  informationText?: string; // Detailed guidance text for UI (max 5000 chars)
  sectionName?: string;
  displayOrder?: number;
  /** The effective rule. Nothing is inherited at runtime — the category's
   *  rule was copied down to every requirement in AG-249's migration. */
  filterRule?: import('./farm-profile').FilterRule | null;
}

// Nytt interface för att skapa kontrollinstanser
export interface CreateControlInstanceRequest {
  name: string; // t.ex. "KRAV-kontroll 2025-09-15"
  description?: string;
  controlTypeId: string; // Välj från fördefinierade kontrolltyper
  scheduledDate?: string;
  affarspartnerIds: string[]; // At least one required
}

// Bakåtkompatibilitet - behåll under refaktoreringen
export interface CreateControlRequest extends CreateControlInstanceRequest {
  controlType: ControlType;
}

export interface ControlFilters {
  search?: string;
  status?: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'CANCELLED' | 'ARCHIVED';
  certificationType?: 'KRAV' | 'EU_ORGANIC' | 'IP' | 'GLOBAL_GAP' | 'BRC' | 'IFS' | 'SQF';
  controlType?: 'INTERNAL' | 'EXTERNAL' | 'CERTIFICATION_BODY';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  sortBy?: 'name' | 'dueDate' | 'createdAt' | 'priority' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface ControlStats {
  totalControls: number;
  activeControls: number;
  completedControls: number;
  overdueControls: number;
  upcomingControls: number;
  controlTypeDistribution: {
    type: string;
    count: number;
  }[];
  recentControls: Control[];
}

// Share types
export interface Share {
  id: string;
  recipientEmail: string;
  accessLevel: AccessLevel;
  accessToken: string;
  expiresAt: string;
  isActive: boolean;
  createdAt: string;
  lastAccessed?: string;
  accessCount: number;
  control: Control;
  shareUrl?: string;
  plainPassword?: string; // Only returned on creation for one-time display
}

export interface CreateShareRequest {
  controlId: string;
  recipientEmail: string;
  accessLevel: AccessLevel;
  expiresAt: string;
}

export enum ControlTypeEnum {
  KRAV_ORGANIC = 'KRAV_ORGANIC',
  EU_ORGANIC = 'EU_ORGANIC',
  IP_INTEGRATED = 'IP_INTEGRATED',
  GOVERNMENT_INSPECTION = 'GOVERNMENT_INSPECTION',
  INTERNAL_AUDIT = 'INTERNAL_AUDIT',
  CUSTOMER_AUDIT = 'CUSTOMER_AUDIT',
  CERTIFICATION_RENEWAL = 'CERTIFICATION_RENEWAL',
  SPOT_CHECK = 'SPOT_CHECK',
  COMPLAINT_INVESTIGATION = 'COMPLAINT_INVESTIGATION',
  OTHER = 'OTHER',
}

export enum ControlStatus {
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',  // Computed status from backend
  CANCELLED = 'CANCELLED',
  ARCHIVED = 'ARCHIVED',
}

export enum AccessLevel {
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
  COMMENT = 'COMMENT',
}

// UI types
export interface SnackbarMessage {
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  action?: React.ReactNode;
}

export interface LoadingState {
  isLoading: boolean;
  message?: string;
}

export interface ErrorState {
  hasError: boolean;
  message?: string;
  details?: any;
}

// Form types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'select' | 'multiselect' | 'date' | 'file';
  required?: boolean;
  options?: { value: string; label: string }[];
  validation?: any;
}

// Form field types for control view (AG-200)
export type FieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'SELECT' | 'MULTISELECT' | 'FILE' | 'EMAIL' | 'URL' | 'PHONE' | 'TEXTAREA';

export interface FormFieldDefinition {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  defaultValue?: any;
  required: boolean;
  validation?: { type: string; value: any; message: string }[];
  layoutWidth?: 3 | 4 | 6 | 12;
  sectionId?: string;
  unit?: string; // Suffix for NUMBER fields (e.g. "ha", "kg", "st")
  optionStyle?: 'inline' | 'card'; // Display style for SELECT/MULTISELECT options
  inputPlacement?: 'beside' | 'below'; // Input field placement relative to label
  /**
   * Farm-profile filter. When the rule fails for the farm, the field is hidden
   * unless the farmer re-enabled it (see ControlDto.activatedFieldIds).
   */
  filterRule?: import('./farm-profile').FilterRule | null;
  /**
   * When true the farmer may attach documents to this field in addition to (or
   * instead of) typing an answer. The field counts as answered when either is present.
   */
  allowUpload?: boolean;
  /**
   * Tags the uploaded document gets. Required when allowUpload is true: the
   * document gets exactly these, and without them it would match nothing.
   */
  uploadTags?: string[];
  /** Validity mode pre-selected when uploading against this field. */
  validityMode?: CategoryValidityMode | null;
  // These fields enable integration with document requirement sections
  sectionName?: string;
  displayOrder?: number;
}

export interface FormFieldEntry {
  fieldId: string;
  value: string | number | boolean | string[] | null;
  answeredAt: string | null; // ISO timestamp, canonical "is answered" signal
  fieldType: FieldType;
  fieldLabel: string;
}

export interface FormConfiguration {
  fields: FormFieldDefinition[];
  sections?: { id: string; title: string; description?: string; order: number }[];
}

// Re-export FormSection from FormFieldEditor for backward compatibility
export type { FormSection } from '@/components/admin/FormFieldEditor';

// Query types
export interface QueryOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, any>;
}

// Route types
export interface RouteConfig {
  path: string;
  component: React.ComponentType;
  protected?: boolean;
  exact?: boolean;
  title?: string;
}

// Admin types (AdminUser and AdminUserDetails defined above at lines 92-105)

export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalControls: number;
  activeControls: number;
  totalDocuments: number;
  recentActivity: AdminActivity[];
  systemHealth: SystemHealth;
}

export interface AdminActivity {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string;
  timestamp: string;
  user?: {
    id: string;
    email: string;
  };
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  database: {
    status: 'connected' | 'disconnected';
    responseTime?: number;
  };
  storage: {
    status: 'healthy' | 'warning' | 'error';
    usagePercentage: number;
  };
  uptime: number;
}

// Unified Control Type Request interfaces
export interface CreateControlTypeRequest {
  name: string;
  issuerId: string;
  version?: string;
  description: string;
  formConfiguration?: any;
  inspectionFrequency?: string;
  notes?: string;
  activeFrom?: string;
  activeUntil?: string;
  /** Omitted: the year before the control (DEFAULT_PERIOD_CONFIG). */
  periodMode?: PeriodMode;
  periodYearOffset?: number;
  periodMonths?: number | null;
}

export interface UpdateControlTypeRequest {
  name?: string;
  issuerId?: string;
  description?: string;
  formConfiguration?: any;
  inspectionFrequency?: string;
  notes?: string;
  isActive?: boolean;
  activeFrom?: string;
  /** null explicitly clears the end date (open-ended); undefined = unchanged. */
  activeUntil?: string | null;
  /** The control period (AG-298). `periodMonths: null` clears the months;
   *  an omitted field is unchanged. Rolling mode without months is a 400. */
  periodMode?: PeriodMode;
  periodYearOffset?: number;
  periodMonths?: number | null;
}

export interface CloneControlTypeRequest {
  newVersion: string;
  newName?: string;
  issuerId?: string;
}

// DEPRECATED: Legacy interfaces for backward compatibility during migration
export interface VersionedControlType extends ControlType {
  parentVersion?: string;
  changeLog?: any[];
}

export interface CreateVersionedControlTypeRequest extends CreateControlTypeRequest {}

export interface UpdateVersionedControlTypeRequest extends UpdateControlTypeRequest {}

export interface CloneVersionedControlTypeRequest extends CloneControlTypeRequest {}

// Audit Log types
export interface AuditLogEntry {
  id: string;
  userId?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
  details?: Record<string, any>;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    farmProfile?: {
      farmName?: string;
    };
  };
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditStats {
  totalLogs: number;
  todayLogs: number;
  criticalEvents: number;
  uniqueUsers: number;
  actionBreakdown: Record<string, number>;
  resourceTypeBreakdown: Record<string, number>;
}

export interface AuditLogFilters {
  userId?: string;
  resourceType?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  search?: string;
}

// Verification Method types for requirement status
export enum VerificationMethod {
  UPLOAD_REQUIRED = 'UPLOAD_REQUIRED',
  CHECKED_ON_FARM = 'CHECKED_ON_FARM',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export interface RequirementStatus {
  id: string;
  controlId: string;
  requirementId: string;
  verificationMethod: VerificationMethod;
  notes?: string;
  setBy: string;
  /** The control this status was confirmed from in a renewal (AG-253), else null. */
  carriedFromControlId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateRequirementStatusRequest {
  verificationMethod: VerificationMethod;
  notes?: string;
}

/**
 * How an upload-enabled form field is handled on one control — the form-field
 * twin of {@link RequirementStatus}. `fieldId` is FormFieldDefinition.id inside
 * the control type's formConfiguration, not a database id.
 */
export interface FormFieldStatus {
  id: string;
  controlId: string;
  fieldId: string;
  verificationMethod: VerificationMethod;
  notes?: string;
  setBy: string;
  /** The control this status was confirmed from in a renewal (AG-253), else null. */
  carriedFromControlId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Renewal from the previous control (AG-253) ───────────────────────────

/**
 * The previous control of the same type on the konto, as
 * `GET /controls/types/:id/previous` returns it. Null when there is none.
 */
export interface PreviousControl {
  id: string;
  name: string;
  /** `YYYY-MM-DD`, or null when the control has no date. */
  scheduledDate: string | null;
  status: string;
}

export type ProposalStatusKind = 'requirement' | 'field';

export interface ProposedStatus {
  kind: ProposalStatusKind;
  /** requirementId or fieldId. */
  id: string;
  label: string;
  /** The requirement's section, for grouping in the summary. */
  sectionName?: string;
  /** Both farmer-set statuses carry over as they are, note included; UPLOAD_REQUIRED is never proposed. */
  verificationMethod: 'NOT_APPLICABLE' | 'CHECKED_ON_FARM';
  notes: string | null;
}

export interface ProposedActivation {
  kind: ProposalStatusKind;
  id: string;
  label: string;
  sectionName?: string;
  verificationMethod?: 'NOT_APPLICABLE' | 'CHECKED_ON_FARM';
  notes?: string | null;
}

export interface ProposedAnswer {
  fieldId: string;
  label: string;
  fieldType: string;
  value: unknown;
  originalEntryId: string;
}

/** Informational row in the summary: a document from the previous control, linked or only suggested. */
export interface ProposedDocument {
  documentId: string;
  originalName: string;
  requirementId?: string;
  fieldId?: string;
  label: string;
  /** The requirement's section, for grouping in the summary. */
  sectionName?: string;
  outcome: 'linked' | 'suggested';
  reason: string;
  /** The Swedish one-liner ("Giltigt till 2027-03-01", "Utgick 2026-01-31"). */
  text: string;
}

/**
 * What the previous control offers for the farmer to confirm in the summary
 * after creation. Computed by the backend, never persisted: nothing is written
 * until `POST /controls/:id/renewal` names the selection.
 */
export interface RenewalProposal {
  source: PreviousControl;
  statuses: ProposedStatus[];
  activations: ProposedActivation[];
  answers: ProposedAnswer[];
  documents: ProposedDocument[];
}

/** The farmer's selection, sent to `POST /controls/:id/renewal`. */
export interface ApplyRenewalRequest {
  statuses: Array<{ kind: ProposalStatusKind; id: string }>;
  activations: Array<{ kind: ProposalStatusKind; id: string }>;
  /** Field ids of the answers to copy. */
  answers: string[];
}

export interface ApplyRenewalResult {
  statusesWritten: number;
  activationsWritten: number;
  answersWritten: number;
}

// Cross-control document sharing types

/**
 * Source type for tracking how a document was added to a control
 */
export enum SourceType {
  UPLOADED = 'UPLOADED', // User uploaded directly to this control
  LINKED = 'LINKED', // Auto-linked from another control
}

/**
 * Information about a control that a document is linked to
 */
export interface LinkedControlInfo {
  controlId: string;
  controlName: string;
  requirementId: string;
}

/** One requirement in one open control that keeps a tag on a document. */
export interface LockReason {
  controlId: string;
  controlName: string;
  requirementId: string;
  requirementDescription: string;
}

/** A tag the farmer may not remove, with every link that requires it. */
export interface LockedTag {
  slug: string;
  requiredBy: LockReason[];
}

/**
 * One tag set ("taggrupp") of a document that open controls' requirements rest
 * on, with the slugs inside it that are locked. Only sets with at least one
 * locked slug are listed; any other set may be removed whole.
 */
export interface LockedTagSet {
  /** Index of the set in `documentTagSets`. */
  setIndex: number;
  /** The set's slugs. */
  tags: string[];
  lockedTags: LockedTag[];
}

/**
 * Body of `PUT /documents/:id`: the upload request minus `tags`, plus the
 * document's complete list of tag sets. Omitting `tagSets` leaves the tags
 * unchanged.
 */
export type DocumentUpdateRequest = Omit<Partial<DocumentUploadRequest>, 'tags'> & {
  tagSets?: string[][];
};

/**
 * Detailed sharing information for a document.
 *
 * Returned by `GET /documents/:id/sharing`. Powers both the version-aware
 * delete confirmation dialog and the "Kopplingar"-popover on doc rows.
 */
export interface DocumentSharingInfo {
  documentId: string;
  documentName: string;
  /** Tag slugs; the popover resolves the names. Empty = "Okategoriserad". */
  documentTags: string[];
  /**
   * The document's tag sets. Optional only for the deploy window where the API
   * predates tag groups — read as `documentTagSets ?? [documentTags]`.
   */
  documentTagSets?: string[][];
  controls: Array<{
    controlId: string;
    controlName: string;
    /** Display name of the control's controlType, e.g. "Myndighetskontroll". */
    controlTypeName: string | null;
    /** Display name of the control's issuer, e.g. "KRAV Ekologisk". */
    issuerName: string | null;
    /** ISO date the control itself is scheduled for — disambiguates
     *  same-named controls in the popover (e.g. two
     *  "Revision Skånemejerier AB" instances in different years). */
    controlScheduledDate: string | null;
    sourceType: SourceType;
    sourceControlId: string | null;
    sourceControlName: string | null;
    /** ISO timestamp of when the link was created. */
    linkedAt: string;
    /** Lifecycle status of the control; only DRAFT and IN_PROGRESS controls lock tags. */
    controlStatus: string;
    /** The requirement the link answers; null for field links and plain links. */
    requirementId: string | null;
    requirementDescription: string | null;
  }>;
  totalControls: number;
  /**
   * Per tag set, the tags that open controls' requirements rest on. Computed
   * by the backend (the same computation rejects a PUT that removes one), the
   * frontend never recomputes it. Optional only for the deploy window; then
   * read `lockedTags` as the locks of set 0.
   */
  lockedTagSets?: LockedTagSet[];
  /** @deprecated Pre-tag-group API: the locks of the single tag set. Use `lockedTagSets`. */
  lockedTags?: LockedTag[];
  /** Matching controls not auto-linked because they have an active share. */
  skippedSharedControls: Array<{
    controlId: string;
    controlName: string;
    controlTypeName: string | null;
    controlScheduledDate: string | null;
  }>;
  skippedSharedCount: number;
}

/**
 * Response from adding a document to a control with auto-linking
 */
export interface AddDocumentToControlResponse {
  success: boolean;
  message: string;
  /**
   * True when the exact link already existed, so nothing was created, tagged
   * or fanned out. A repeated add is a no-op: the pärm link dialog sends one
   * request per requirement and the first request's fan-out may already have
   * created the next one.
   */
  alreadyLinked?: boolean;
  /** One entry per created LINK: a control linked under two requirements appears twice. */
  linkedControls: LinkedControlInfo[];
  /** Number of DISTINCT controls that received at least one new link. */
  linkedCount: number;
  /**
   * Requirement ids additionally linked inside the control the document was
   * added to (a second tag group answering a sibling requirement). Not part of
   * `linkedControls`, so `linkedCount` stays "other controls".
   */
  sourceControlRequirementIds?: string[];
  /** IDs of matching controls skipped because they have an active share.
   *  Multi-document flows dedupe these across documents before reporting. */
  skippedSharedControlIds: string[];
  /** The same controls by name, for "Inte tillagd i KRAV 2025 (delad)". Optional for older responses. */
  skippedSharedControls?: Array<{ controlId: string; controlName: string }>;
  /** IDs of matching controls that already had this document. */
  alreadyLinkedControlIds: string[];
  /** Matching controls skipped because they have an active share. */
  skippedSharedCount: number;
  /** Matching controls that already had this document (explains 0 new links). */
  alreadyLinkedCount: number;
  /** Matching controls left unlinked because the auto-link cap was reached. */
  cappedControlIds: string[];
  cappedCount: number;
  /**
   * Set when the document's tag sets did not cover the question's tags and the
   * backend added a set (the question's own tags) to the document.
   */
  addedTagSet?: string[];
}

/** Cross-control link outcome of a tag edit; the add-to-control response minus its envelope. */
export type CrossControlLinkSummary = Omit<AddDocumentToControlResponse, 'success' | 'message'>;

/**
 * Response from `PUT /documents/:id`. `crossControlLinks` is null when no
 * auto-link ran (no tag was added), distinct from a run that found nothing.
 */
export interface UpdateDocumentResult {
  document: Document;
  crossControlLinks: CrossControlLinkSummary | null;
}

/**
 * Response from unlinking a document from a control
 */
export interface UnlinkDocumentResponse {
  success: boolean;
  message: string;
  remainingControlsCount: number;
}